import { DeliveryAndNotificationInformationFieldNames } from '@contracts/order/fields/DeliveryAndNotificationInformationFields';
import { DeliveryCommunicationFrequency } from '@contracts/order/valueNormalizers/DeliveryCommunicationFrequency';
import { DeliveryTypeOption } from '@contracts/order/valueNormalizers/DeliveryTypeOption';
import {
  DropdownField,
  EmailField,
  OptionalBooleanField,
  OptionalStringField,
  StringField,
} from '@src/permitField/PermitFields';
import type { AnyObject, StringSchema } from 'yup';
import * as yup from 'yup';

const nonDriverDeliveryTypeOptions = Object.values(DeliveryTypeOption).filter(
  (option) => option !== DeliveryTypeOption.SMS,
);

export class PrimaryDeliveryTypeField extends DropdownField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.PrimaryDeliveryType;
  public static options = nonDriverDeliveryTypeOptions;
  public static validator = yup
    .string()
    .oneOf(PrimaryDeliveryTypeField.options)
    .required();
}

export class SecondaryDeliveryTypeField extends DropdownField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.SecondaryDeliveryType;
  public static options = nonDriverDeliveryTypeOptions;
  public static validator = yup
    .string()
    .oneOf(SecondaryDeliveryTypeField.options);
}

export class NotificationEmailAddressField extends EmailField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.NotificationEmailAddress;
}

export class NotificationMobilePhoneField extends StringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.NotificationMobilePhone;
}

const validateEmails = (
  schema: StringSchema<string | undefined, AnyObject, undefined, ''>,
) =>
  schema
    .trim()
    .test({
      message: 'isEmails',
      test: (value) => {
        if (!value) {
          return true;
        }
        const emails = value.split(';').map((email) => email.trim());
        return emails.every((email) => yup.string().email().isValidSync(email));
      },
    })
    .required();

export class PrimaryDeliveryTypeValueField extends StringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.PrimaryDeliveryTypeValue;
  public static validator: StringSchema = yup
    .string()
    .when(PrimaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.EMAIL,
      then: validateEmails,
    })
    .when(PrimaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.FAX,
      then: (schema) => schema.trim().required(),
    })
    .when(PrimaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.MAIL,
      then: (schema) => schema.trim().required(),
    })
    .when(PrimaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.WEB_DOWNLOAD,
      then: (schema) => schema.notRequired(),
    });
}

export class SecondaryDeliveryTypeValueField extends StringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.SecondaryDeliveryTypeValue;
  public static validator: StringSchema = yup
    .string()
    .when(SecondaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.EMAIL,
      then: validateEmails,
    })
    .when(SecondaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.FAX,
      then: (schema) => schema.trim().required(),
    })
    .when(SecondaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.MAIL,
      then: (schema) => schema.trim().required(),
    })
    .when(SecondaryDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.WEB_DOWNLOAD,
      then: (schema) => schema.notRequired(),
    });
}

export class DriverDeliveryField extends OptionalStringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.DriverDelivery;
}
export class DriverDeliveryTypeField extends StringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.DriverDeliveryType;
  public static validator: StringSchema = yup
    .string()
    .when(DriverDeliveryField.fieldName, {
      is: (value: string) => Boolean(value),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    });
}
export class DriverDeliveryNameField extends OptionalStringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.DriverDeliveryName;
}

export class DriverDeliverySmsAuthorizationField extends OptionalBooleanField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.DriverDeliverySmsAuthorization;
  public static validator = yup
    .boolean()
    .transform((value) => (value === '' ? false : value))
    .when(DriverDeliveryTypeField.fieldName, {
      is: DeliveryTypeOption.SMS,
      then: (schema) => schema.oneOf([true]).required(),
      otherwise: (schema) => schema.notRequired(),
    });
}

export class DriverDeliveryCommunicationFrequency extends DropdownField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.DriverDeliveryCommunicationFrequency;
  public static options = Object.values(DeliveryCommunicationFrequency);
  public static validator = yup
    .string()
    .oneOf(DriverDeliveryCommunicationFrequency.options)
    .when(DriverDeliveryField.fieldName, {
      is: (value: string) => Boolean(value),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    });
}

export class PermitContactNameField extends StringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.PermitContactName;
}

export class PermitContactPhoneField extends StringField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.PermitContactPhone;
}

export class PermitContactEmailAddressField extends EmailField {
  public static fieldName: string =
    DeliveryAndNotificationInformationFieldNames.PermitContactEmailAddress;
}

export const permitDeliveryAndNotificationsFields = {
  [PrimaryDeliveryTypeField.fieldName]: PrimaryDeliveryTypeField.validator,
  [SecondaryDeliveryTypeField.fieldName]: SecondaryDeliveryTypeField.validator,
  [PrimaryDeliveryTypeValueField.fieldName]:
    PrimaryDeliveryTypeValueField.validator,
  [SecondaryDeliveryTypeValueField.fieldName]:
    SecondaryDeliveryTypeValueField.validator,
  [DriverDeliveryTypeField.fieldName]: DriverDeliveryTypeField.validator,
  [DriverDeliveryField.fieldName]: DriverDeliveryField.validator,
  [DriverDeliveryNameField.fieldName]: DriverDeliveryNameField.validator,
  [DriverDeliverySmsAuthorizationField.fieldName]:
    DriverDeliverySmsAuthorizationField.validator,
  [DriverDeliveryCommunicationFrequency.fieldName]:
    DriverDeliveryCommunicationFrequency.validator,
};

export const orderInformationFields = {
  [PermitContactNameField.fieldName]: PermitContactNameField.validator,
  [PermitContactPhoneField.fieldName]: PermitContactPhoneField.validator,
  [PermitContactEmailAddressField.fieldName]:
    PermitContactEmailAddressField.validator,
};
